import { useEffect, useRef } from "react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";

// Google Places address autocomplete styled as an .rg-input. Degrades to a plain text input when the
// Places library is unavailable (no Maps key configured), so the form always works. Mirrors the admin
// SPA's AddressAutocomplete. `useMapsLibrary` returns null (no throw) when there is no <APIProvider>.
export function AddressAutocomplete({
  value,
  onChange,
  placeholder,
  testId,
  required,
}: {
  value: string;
  onChange: (address: string) => void;
  placeholder?: string;
  testId?: string;
  required?: boolean;
}) {
  const places = useMapsLibrary("places");
  const ref = useRef<HTMLInputElement>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!places || !ref.current) return;
    const ac = new places.Autocomplete(ref.current, {
      types: ["address"],
      componentRestrictions: { country: "us" },
      fields: ["address_components", "geometry", "name", "formatted_address"],
    });
    const listener = ac.addListener("place_changed", () => {
      const place = ac.getPlace();
      const text = place.formatted_address || place.name || "";
      if (text) onChangeRef.current(text);
    });
    return () => listener.remove();
  }, [places]);

  return (
    <input
      ref={ref}
      className="rg-input"
      type="text"
      value={value}
      required={required}
      autoComplete="off"
      data-testid={testId}
      placeholder={placeholder ?? (places ? "Start typing an address…" : "Street address")}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}
